import { hamletUser } from "../../../common/hamlet/hamlet-user";
import React, { useEffect, useState } from "react";
import QuizRouter from "./QuizRouter";
import { HashRouter } from "react-router-dom";
import storage from "../../../utils/storage";
import tracking from '../../../utils/tracking';

export const QuizContext = React.createContext();

export default function ConfigurableQuizContainer({
  cards,
  scores,
  labels,
  firstPage,
  lastPageID,
  domRootId,
  maximumQuestions
}) {

  useEffect(() => {
    if (storage.getCookie("login_user_detected") === "true") {
      hamletUser.getStargateProfile(true).then(() => {
        setFirstName(hamletUser.getUser().myDetails.firstName);
      });
    }
  }, []);

  const freshCards = deepCopy(cards);
  const firstCard = getFirstCard(freshCards);

  const [accruedCards, setAccruedCards] = useState([]);
  const [quizCards, setQuizCards] = useState(freshCards);
  const [currentCard, setCurrentCard] = useState(firstCard);
  const [currStep, setCurrStep] = useState(0);
  const [prevStep, setPrevStep] = useState(-1);
  const [firstName, setFirstName] = useState("");


  function deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  function getFirstCard(cardStack) {
    return cardStack[Object.keys(cardStack)[0]]
  }

  function goToNextCard() {
    let currentAnswers = [];
    Object.keys(currentCard.choices).forEach(choice => {
      const currChoice = currentCard.choices[choice];
      let answerVal = currChoice.englishTitle + (currChoice.openEnded ? ` | ${currChoice.openEndedValue}` : '');
      if (currChoice.isChecked) {
        currentAnswers.push(answerVal);
      }
    });
    tracking.trackEvent('clicked', {
      eventComponent: 'quizNextButton',
      quizStep: currStep,
      quizQuestion: currentCard.englishTitle,
      quizAnswers: currentAnswers
    });

    const selectedChoice = Object.keys(currentCard.choices).find(choice => currentCard.choices[choice].isChecked);
    const newAccrued = [...accruedCards];
    newAccrued.push(currentCard);
    setAccruedCards(newAccrued);
    const selectedChoiceCard = currentCard.choices[selectedChoice];
    const nextConditionalCard = selectedChoiceCard.nextCard;
    const goNext = currentCard.isConditional ? nextConditionalCard : currentCard.nextCard;
    const nextPage = quizCards[goNext];
    setPrevStep(currStep);
    setCurrStep(currStep + 1);
    if (nextPage) {
      setCurrentCard(nextPage);
    } else {
      finishQuiz();
    }
    document.getElementById(domRootId).scrollIntoView({ behavior: 'smooth' });
  }

  function goToPreviousCard() {
    tracking.trackEvent('clicked', {
      eventComponent: 'quizPreviousButton',
      quizStep: currStep,
      quizQuestion: currentCard.englishTitle
    });
    const newAccrued = [...accruedCards];
    const prevCard = newAccrued.pop();
    setAccruedCards(newAccrued);
    setPrevStep(currStep);
    setCurrStep(currStep - 1);
    setCurrentCard(prevCard);
    document.getElementById(domRootId).scrollIntoView({ behavior: 'smooth' });
  }

  function finalScoreCalculations(returnType) {
    let score = 0;
    let answerKeys = [];
    let classes = [];
    let trackInfo = [];
    let trackFormatted = "";
    accruedCards.forEach((card, index) => {
      let accruedAnswers = [];
      trackFormatted += `\n${index}. ${card.title}\n`;
      Object.keys(card.choices).forEach(choice => {
        const cardChoice = card.choices[choice];
        if (cardChoice.isChecked) {
          const answerString = cardChoice.englishTitle + (cardChoice.openEnded ? ` | ${cardChoice.openEndedValue}` : '')
          accruedAnswers.push(answerString);
          answerKeys.push(choice);
          classes.push(cardChoice.choiceId);
          score += cardChoice.value;
          trackFormatted += `- ${answerString}\n`
        }
      });
      trackInfo.push({
        questionTitle: card.englishTitle,
        questionAnswers: accruedAnswers
      });
    })

    if (returnType == "answers") {
      return answerKeys;
    } else if (returnType == "classes") {
      return classes;
    } else if (returnType == "trackingInfo") {
      return trackInfo;
    } else if (returnType == "formatted") {
      return trackFormatted;
    }
    return score;
  }

  function getResults(returnTitles) {
    const thisURL = new URLSearchParams(window.location.hash.replace('#/share', ''));

    let finalScore = []
    let finalAnswerClasses = [];
    if (accruedCards.length == 0 && thisURL.get('s') && thisURL.get('a')) {
      finalScore = isNaN(thisURL.get('s')) ? 0 : Number(thisURL.get('s'));
      finalAnswerClasses = thisURL.get('a') ? thisURL.get('a').split(',') : [];
    } else if (accruedCards.length) {
      finalScore = finalScoreCalculations();
      finalAnswerClasses = finalScoreCalculations("classes");
    }

    let scoreMatches = scores.filter(score => {
      let matchesAnswer = true;
      if (score.answerSpecific) {
        matchesAnswer = score.answers.some(answer => finalAnswerClasses.includes(answer));
      }
      return finalScore >= score.minVal && finalScore < score.maxVal & matchesAnswer;
    });

    let scoreContentString = scoreMatches.map((scoreMatch) => {
      return scoreMatch.content;
    }).join("");

    const scoreMinClasses = scoreContentString.match(/score-min-([\d]*)[\s"]/g) ? scoreContentString.match(/score-min-([\d]*)[\s"]/g) : [];
    const scoreMaxClasses = scoreContentString.match(/score-max-([\d]*)[\s"]/g) ? scoreContentString.match(/score-max-([\d]*)[\s"]/g) : [];

    scoreMinClasses.forEach(scoreClass => {
      const minRegEx = new RegExp(scoreClass, "g");
      const appender = scoreClass.endsWith('"') ? '"' : ' ';
      const minScore = Number(scoreClass.replace('score-min-', '').replace(appender, ''));
      if (finalScore >= minScore) {
        scoreContentString = scoreContentString.replace(minRegEx, appender);
      }
    });

    scoreMaxClasses.forEach(scoreClass => {
      const maxRegEx = new RegExp(scoreClass, "g");
      const appender = scoreClass.endsWith('"') ? '"' : ' ';
      const maxScore = Number(scoreClass.replace('score-max-', '').replace(appender, ''));
      if (finalScore <= maxScore) {
        scoreContentString = scoreContentString.replace(maxRegEx, appender);
      }
    });

    finalAnswerClasses.forEach(finalAnswer => {
      const finalAnswerRegEx = new RegExp(finalAnswer, "g");
      scoreContentString = scoreContentString.replace(finalAnswerRegEx, "force-show");
    });
    if (returnTitles) {
      return scoreMatches.map(function (match) { return match.title; });
    }
    return scoreContentString;
  }

  function finishQuiz() {
    window.location.hash = `#/results`;
    document.getElementById(domRootId).scrollIntoView({ behavior: 'smooth' });
  }

  function restartQuiz() {
    const newCards = deepCopy(cards);
    const firstNewCard = getFirstCard(newCards);
    setPrevStep(-1);
    setCurrStep(0);
    setQuizCards(newCards);
    setCurrentCard(firstNewCard);
    setAccruedCards([]);
  }

  function cardChoiceChange(choiceKey) {
    const currCard = { ...currentCard };
    let val;
    if (currCard.cardType == "checkbox") {
      val = !currCard.choices[choiceKey].isChecked;
    } else {
      for (let newKey in currCard.choices) {
        currCard.choices[newKey].isChecked = false;
      }
      val = true;
    }
    currCard.choices[choiceKey].isChecked = val;
    setCurrentCard(currCard);
  }

  function updateChoiceValue(choiceKey, event) {
    const currCard = { ...currentCard };
    currCard.choices[choiceKey].openEndedValue = event.target.value;
    setCurrentCard(currCard);
  }

  function toggleCardContent() {
    const currCard = { ...currentCard };
    currCard.isShowingExtraContent = !currCard.isShowingExtraContent;
    setCurrentCard(currCard);
  }

  return (
    <QuizContext.Provider value={{
      labels,
      prevStep,
      currStep,
      firstName,
      firstPage,
      domRootId,
      lastPageID,
      getResults,
      restartQuiz,
      currentCard,
      goToNextCard,
      accruedCards,
      maximumQuestions,
      goToPreviousCard,
      cardChoiceChange,
      updateChoiceValue,
      toggleCardContent,
      finalScoreCalculations
    }}>
      <HashRouter>
        <QuizRouter />
      </HashRouter>
    </QuizContext.Provider>
  )
}
